import { authService } from '@rfc/rfc-components-library/services'
import type { Socket } from 'socket.io-client'
import type { DetectionModel } from '@/types/models/DetectionModel'

export const SOCKET_RETRY_DELAY = 5000

export type SocketDetectionsId = 'checkpoint_id' & 'event_id' & 'device_id'

export const buildDetectionArray = <T extends { detections: DetectionModel[] }>(
    detections: DetectionModel[],
    itemsList: T[],
    key: keyof T & keyof DetectionModel
) =>
    itemsList.reduce((prev: T[], currentItem: T) => {
        const filteredList = (detections as DetectionModel[]).filter(
            item => item[key] === currentItem[key]
        )
        if (!filteredList.length) return [...prev, currentItem]
        return [
            ...prev,
            {
                ...currentItem,
                detections: [
                    ...filteredList,
                    ...(currentItem.detections?.slice(0, 3 - filteredList.length) ?? [])
                ]
            }
        ]
    }, [])

export const buildSocketStateArray = <T extends { detections: DetectionModel[] }>(
    list: number[],
    detections: T[],
    key: keyof T
) =>
    list.map(id => ({
        [key]: id,
        detections: detections.find(item => item[key] === id)?.detections ?? []
    }))

export const SOCKET_AUTH_ERROR = 'Authentication error'

export const MAX_SOCKET_AUTH_RETRIES = 5

/**
 * The ws-export auth middleware rejects with `Authentication error: <reason>`, and the airscan
 * driver with the bare prefix, so the reason must not be part of the comparison.
 */
export const isSocketAuthError = (error?: { message?: string } | string | null) => {
    const message = typeof error === 'string' ? error : error?.message
    return Boolean(message?.startsWith(SOCKET_AUTH_ERROR))
}

/**
 * Recovers a socket that the ws-export auth middleware rejected.
 *
 * Two things have to happen, and neither happens on its own:
 * - socket.io destroys a socket rejected by a server middleware (`socket.active` becomes false)
 *   before emitting `connect_error`, so it never reconnects unless `connect()` is called again.
 * - the access token is only ever renewed by the axios interceptor reacting to a REST 401, which
 *   never comes while a user idles on a page. `authService.me()` provokes that 401 so the
 *   interceptor renews the token — and de-duplicates concurrent renewals — before we reconnect.
 *
 * Retries are capped and delayed so a token the server keeps rejecting cannot spin into a loop
 * that hammers the auth service.
 */
export const createSocketAuthRecovery = (getSocket: () => Socket) => {
    let attempts = 0
    let timer: ReturnType<typeof setTimeout> | undefined

    const cancel = () => {
        if (timer) clearTimeout(timer)
        timer = undefined
    }

    const reset = () => {
        attempts = 0
        cancel()
    }

    const recover = async (error?: { message?: string } | string | null) => {
        if (!isSocketAuthError(error) || attempts >= MAX_SOCKET_AUTH_RETRIES) return false
        attempts++
        try {
            await authService.me()
        } catch {
            // The interceptor already tried to renew the token, failed, and logged the user out.
            return false
        }
        cancel()
        timer = setTimeout(() => getSocket().connect(), SOCKET_RETRY_DELAY)
        return true
    }

    return { recover, reset, cancel }
}
